import React, { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { buttonClasses } from "../../../components/ui";
import logo from "../../../assets/svg/auth/logo.svg";
// The PNG rather than the SVG beside it, which is a bare gradient: this one
// carries the glass object the panel was drawn with, at the resolution it was
// rendered at.
import panelArt from "../../../assets/svg/free trial/free trial conformation page image.png";
import { ROUTES } from "../../../routes/paths";
import { formatCreatedOn } from "../../brand/planFormat";
import GreenCheck from "../components/GreenCheck";
import { CYCLE_LABELS, PRODUCT_NAME } from "../data/subscriptionPlans";
import { usePlans } from "../hooks/usePlans";
import {
  TRIAL_DAYS,
  TRIAL_REMINDER_DAYS,
  cycleParam,
  readTrial,
  trialLastDayOf,
  trialReminderOf,
} from "../trialState";

/** A date as the timeline writes it: `25 Aug`. No year — every stop on it is
 *  inside a fortnight, and the year is already on the card to the left. */
const shortDay = (date: Date): string =>
  date.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });

/** One line of the card: what it is on the left, what it says on the right. */
const Row: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex items-baseline justify-between gap-4">
    <dt className="text-[13px] 3xl:text-[14px] text-ink-muted">{label}</dt>
    <dd className="text-right text-[13px] 3xl:text-[14px] font-medium text-ink">
      {children}
    </dd>
  </div>
);

/** One stop on the timeline in the panel — a dot, the line down from it, and
 *  what happens on that day. */
const Stop: React.FC<{
  title: string;
  detail: string;
  /** The stop that has already happened is filled white and a size larger; the
   *  two still to come are quieter. */
  now?: boolean;
  /** The last one, so no line continues past its dot. */
  last?: boolean;
}> = ({ title, detail, now = false, last = false }) => (
  <li className="flex gap-4">
    <div className="flex flex-col items-center" aria-hidden="true">
      <span
        className={
          now
            ? "mt-1.5 h-3.5 w-3.5 flex-shrink-0 rounded-full bg-white"
            : "mt-1.5 h-3 w-3 flex-shrink-0 rounded-full bg-[#B9C7F5]"
        }
      />
      {!last && <span className="w-px flex-1 bg-white/35" />}
    </div>
    <div className={last ? "" : "pb-8"}>
      <p className="text-[16px] 3xl:text-[18px] font-medium text-white">
        {title}
      </p>
      <p className="mt-1 text-[13px] 3xl:text-[14px] leading-[1.6] text-white/80">
        {detail}
      </p>
    </div>
  </li>
);

/**
 * The trial, started — and the last screen before the app itself.
 *
 * It confirms rather than decides. What it draws is the note the detail step
 * left behind: the cycle, and the day the fourteen days run out — dates the
 * *server* set when it started the trial, carried across one navigation rather
 * than fetched again; see `trialState`. A reader who lands here with no such
 * note has not started a trial in this tab, and is sent back to the offer,
 * which asks the backend whether there is still one to be had.
 *
 * Two halves, and they answer two different questions. The left is *what has
 * happened*: a tick, the plan, and the way into the app. The right is *what
 * happens next* — the fortnight laid out as three dated stops, because the one
 * thing a free trial owes anybody is that the day it ends is not a surprise.
 *
 * It draws its own shell rather than using `TrialLayout`, which the two steps
 * before it share: the panel runs to the edge of the window on every side, and
 * a layout built around a centred column with page padding cannot do that.
 *
 * The way onward goes to the dashboard, which is where the sidebar appears for
 * the first time: all three trial screens are drawn outside `AppLayout`, so the
 * rail arriving is what tells someone they are in.
 */
const TrialStartedPage: React.FC = () => {
  const { cycle } = useParams<{ cycle: string }>();
  const cycleId = cycleParam(cycle);

  // Read once, at mount, the way the payment result screen reads its own.
  const [trial] = useState(() => (cycleId ? readTrial(cycleId) : null));

  // The plan, for its name. The list is the seller's own word for what is on
  // sale; where it has not arrived — or never does — this app's own name for
  // the product stands in, because a trial that has already started should not
  // go unnamed over a price list nobody is being shown.
  const { plans } = usePlans();
  const plan = plans.find((candidate) => candidate.id === cycleId);

  if (!cycleId) return <Navigate to={ROUTES.trial} replace />;
  if (!trial) return <Navigate to={ROUTES.trial} replace />;

  const productName = plan?.productName ?? PRODUCT_NAME;
  const cycleLabel = plan?.cycleLabel ?? CYCLE_LABELS[cycleId];
  /** The server's `ends_at`: the instant the fortnight stops, which is the
   *  morning after the last day it covers — so it is the day of the first
   *  charge rather than the last free one. */
  const chargedOn = new Date(trial.endsOn);
  /** The last day the trial actually covers — day fourteen, the day it started
   *  being day one. What "valid till" means, here as on a recharge. */
  const endsOn = trialLastDayOf(chargedOn);
  const remindsOn = trialReminderOf(endsOn);
  /** Which day of the fortnight the reminder falls on — the eleventh of
   *  fourteen, worked out rather than written, so the two constants cannot
   *  disagree with the sentence beside them. */
  const reminderDay = TRIAL_DAYS - TRIAL_REMINDER_DAYS;

  return (
    // Two columns from `lg`, stacked below it with the confirmation first: what
    // has happened outranks what happens next on a screen too narrow for both.
    // `min-h-screen` rather than `h-screen`, so a short window scrolls the page
    // instead of clipping the card.
    <div className="min-h-screen bg-white lg:grid lg:grid-cols-[1.6fr_1fr]">
      <div className="flex flex-col px-5 py-6 sm:px-16 sm:py-10 3xl:px-20 3xl:py-14">
        <header>
          <img src={logo} alt="Naurist" className="h-6 3xl:h-7 w-auto" />
        </header>

        {/* Centred in what is left under the logo, and held to about two thirds
            of the half it sits in — the proportion the screen was drawn at.
            Capped rather than left to fill, because a card of four short rows
            stretched the width of a large monitor stops reading as a card. */}
        <div className="flex flex-1 flex-col justify-center py-10">
          <div className="w-full max-w-[550px] 3xl:max-w-[600px]">
            <GreenCheck />

            <h1 className="mt-6 text-[26px] 3xl:text-[30px] font-semibold leading-tight text-brand-dark">
              Your {TRIAL_DAYS}-day free trial activated!
            </h1>

            <p className="mt-3 text-[14px] 3xl:text-[15px] leading-relaxed text-ink-muted">
              Full access to everything in {productName}. You won&rsquo;t be
              charged until{" "}
              <span className="font-semibold text-ink">
                {formatCreatedOn(chargedOn)}
              </span>
              .
            </p>

            <section
              aria-labelledby="trial-card-heading"
              className="mt-7 rounded-md border border-line bg-white"
            >
              <div className="flex items-center justify-between gap-4 border-b border-line px-5 py-4">
                <div className="min-w-0">
                  <h2
                    id="trial-card-heading"
                    className="text-[15px] 3xl:text-[16px] font-medium text-ink"
                  >
                    {productName}
                  </h2>
                  {/* Which cycle, said quietly under the name. Not in the
                      design, and kept because it is the one fact on this screen
                      that decides what the charge at the end of the fortnight
                      will be — the card would otherwise confirm a trial without
                      ever saying what it is a trial of. */}
                  <p className="mt-0.5 text-[12px] 3xl:text-[13px] text-ink-muted">
                    {cycleLabel} plan
                  </p>
                </div>
                <span className="flex-shrink-0 rounded-full bg-[#E9F9F0] px-2.5 py-1 text-[11px] 3xl:text-[12px] font-semibold tracking-wide text-[#16A34A]">
                  ACTIVE
                </span>
              </div>

              <dl className="flex flex-col gap-3 px-5 py-4 3xl:py-5">
                <Row label="Plan">
                  <span className="text-brand">Trial period</span>
                </Row>
                <Row label="Start day">Today</Row>
                {/* The last day it covers, not the day the charge falls —
                    those are a day apart, and this row is the one somebody
                    reads as "how long have I got". */}
                <Row label="Valid till">{formatCreatedOn(endsOn)}</Row>
              </dl>
            </section>

            <Link
              to={ROUTES.dashboard}
              className={buttonClasses({
                variant: "primary",
                size: "md",
                className: "mt-7",
              })}
            >
              Get started with Naurist
            </Link>
          </div>
        </div>
      </div>

      {/* What happens next, on the panel. The artwork is a gradient authored as
          the whole panel, so it is the background rather than a picture inside
          it — that way it fills whatever height the window gives this column.
          Below `lg` the panel follows the confirmation rather than sitting
          beside it, and takes its height from its own content. */}
      <aside
        style={{ backgroundImage: `url("${panelArt}")` }}
        // Anchored to the bottom, not the middle. The file is a tall panel with
        // the glass object sitting on its bottom edge and bleeding off it; the
        // panel on screen is wider in proportion than the file, so `cover`
        // trims height — and centred, it takes that trim off the bottom and
        // cuts the object in half. Anchoring there spends the crop on the empty
        // gradient at the top instead, which is what that space is for.
        className="flex flex-col  bg-cover bg-bottom px-8 py-14 sm:px-12 3xl:px-16"
      >
        {/* Dropped from the top of the panel rather than centred in it, and by
            how much depends on the window's *height* — 80px on a short one,
            208px from 900px up. It is a height question because that is what
            the gap is competing with: the glass object anchored at the foot of
            the panel. On a tall window there is room to let the two breathe;
            on a laptop the same drop would push the last stop into it.

            `[@media(min-height:900px)]` rather than a `3xl:`-style breakpoint,
            since every named screen in this app measures width. */}
        <h2 className="mt-20 [@media(min-height:900px)]:mt-52 text-[26px] 3xl:text-[30px] font-semibold text-white">
          You&rsquo;re all set
        </h2>
        <p className="mt-2 text-[14px] 3xl:text-[15px] text-white/85">
          Here&rsquo;s how your free trial plays out - no surprises.
        </p>

        <ol className="mt-10 3xl:mt-12">
          <Stop
            now
            title="Today"
            detail={`Full ${productName} access unlocked - build plans, run reports, invite clients.`}
          />
          <Stop
            title={`Day ${reminderDay} • ${shortDay(remindsOn)}`}
            detail={`We'll remind you before the trial ends - ${TRIAL_REMINDER_DAYS} days' notice.`}
          />
          <Stop
            last
            title={`Day ${TRIAL_DAYS} • ${shortDay(endsOn)}`}
            // The design says "or switch to Free, keeping your data" here.
            // There is no Free plan to switch to — a workspace with nothing
            // running keeps its data and can read all of it, but cannot add to
            // it; see `useSubscriptionGate`. So this promises the half that is
            // true and names no tier that does not exist.
            detail="Add a payment method to stay on Pro - your data stays either way."
          />
        </ol>
      </aside>
    </div>
  );
};

export default TrialStartedPage;
